"use client"

/**
 * FRIDGE-1481 — the three sourced blocks on /best-refrigerators, rendered from
 * the object app/[hub-slug]/best-refrigerators-data.ts computed on the server:
 * the ranked table, the defined Best Value table and the brand table. Each
 * prints its method, its count and its "as of" date beside the figures, so a
 * reader (or an assistant quoting the page) can re-derive every number.
 */
import Link from "next/link"
import { PAGE_W, PageSectionHeader } from "./hub-shared"
import { BRAND_TABLE_MIN_RATED, type BestRefrigeratorsRanking, type RankedModel } from "@/lib/best-refrigerators/ranking"

const usd = (cents: number) => "$" + Math.round(cents / 100).toLocaleString("en-US")
const usdWhole = (dollars: number | null) => (dollars === null ? "—" : "$" + Math.round(dollars).toLocaleString("en-US"))
const int = (n: number) => n.toLocaleString("en-US")
const typeLabel = (t: string) => t.replace(/-/g, " ")
const listTypes = (types: string[]) => types.map(typeLabel).join(", ")

const TH = "px-3 py-2 text-left text-[11px] font-bold uppercase tracking-wider text-gray-500 whitespace-nowrap"
const TD = "px-3 py-2.5 text-sm text-gray-800 align-top"
const NUM = `${TD} text-right tabular-nums whitespace-nowrap`

function ModelCell({ r }: { r: RankedModel }) {
  return (
    <>
      <Link href={r.href} className="font-semibold text-gray-900 hover:text-[#0469f0]">
        {r.brand} {r.model}
      </Link>
      <span className="block text-xs text-gray-500 capitalize">{typeLabel(r.type)}</span>
    </>
  )
}

export function RankedModelsSection({ ranking }: { ranking: BestRefrigeratorsRanking }) {
  return (
    <div className={PAGE_W}>
      <section className="my-12" aria-labelledby="ranked-refrigerators">
        <PageSectionHeader eyebrow="Ranked by Owners" title="Best Refrigerators 2026 by Owner Rating" />
        <p className="text-sm text-gray-600 leading-relaxed mb-4 max-w-3xl">
          <strong className="text-gray-900">Method.</strong> Owner rating is the weighted average across the retailer review sources we track for each model, one row per model at its lowest tracked price.
          Only models with at least {int(ranking.minReviews)} owner reviews are ranked, by rating and then by review count. {int(ranking.count)} refrigerators from {int(ranking.brandCount)} brands qualify as of {ranking.asOfLabel}; the {int(ranking.table.length)} highest are shown.
          Annual kWh is the figure published on the model&apos;s EnergyGuide label or ENERGY STAR listing where Fridge.com has it; a dash means none is recorded.
        </p>
        <div className="overflow-x-auto rounded-xl border border-gray-100 bg-white">
          <table className="w-full text-sm" id="ranked-refrigerators">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className={TH}>#</th>
                <th scope="col" className={TH}>Model</th>
                <th scope="col" className={`${TH} text-right`}>Owner rating</th>
                <th scope="col" className={`${TH} text-right`}>Owner reviews</th>
                <th scope="col" className={`${TH} text-right`}>Lowest tracked price</th>
                <th scope="col" className={`${TH} text-right`}>Capacity (cu ft)</th>
                <th scope="col" className={`${TH} text-right`}>Annual kWh</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {ranking.table.map((r) => (
                <tr key={r.id}>
                  <td className={`${TD} font-black text-[#ff9951] tabular-nums`}>{r.rank}</td>
                  <td className={TD}><ModelCell r={r} /></td>
                  <td className={NUM}>{r.rating.toFixed(2)} / 5</td>
                  <td className={NUM}>{int(r.reviews)}</td>
                  <td className={NUM}>{usd(r.priceCents)}</td>
                  <td className={NUM}>{r.capacityCuft === null ? "—" : r.capacityCuft.toFixed(1)}</td>
                  <td className={NUM}>{r.kwhYr === null ? "—" : int(Math.round(r.kwhYr))}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  )
}

export function BestValueSection({ ranking }: { ranking: BestRefrigeratorsRanking }) {
  if (ranking.bestValue.length === 0) return null
  return (
    <div className={PAGE_W}>
      <section className="my-12" aria-labelledby="best-value-refrigerators">
        <PageSectionHeader eyebrow="Best Value, Defined" title="Best Value Refrigerators 2026" />
        <p className="text-sm text-gray-600 leading-relaxed mb-4 max-w-3xl">
          <strong className="text-gray-900">Definition.</strong> Best Value is the lowest price per cubic foot among the {int(ranking.count)} ranked refrigerators that belong to the full-size classes the{" "}
          <Link href="/data/dimensions" className="text-[#0469f0] underline">Fridge.com Refrigerator Dimensions Report</Link> uses ({listTypes(ranking.fullSizeTypes)}) and publish a capacity.
          Price per cubic foot is the lowest tracked price divided by capacity, as of {ranking.asOfLabel}; every model here already carries at least {int(ranking.minReviews)} owner reviews.
        </p>
        <div className="overflow-x-auto rounded-xl border border-gray-100 bg-white">
          <table className="w-full text-sm" id="best-value-refrigerators">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className={TH}>Model</th>
                <th scope="col" className={`${TH} text-right`}>Price per cu ft</th>
                <th scope="col" className={`${TH} text-right`}>Lowest tracked price</th>
                <th scope="col" className={`${TH} text-right`}>Capacity (cu ft)</th>
                <th scope="col" className={`${TH} text-right`}>Owner rating</th>
                <th scope="col" className={`${TH} text-right`}>Owner reviews</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {ranking.bestValue.map((r) => (
                <tr key={r.id}>
                  <td className={TD}><ModelCell r={r} /></td>
                  <td className={`${NUM} font-bold text-gray-900`}>{`$${r.pricePerCuft.toFixed(2)}`}</td>
                  <td className={NUM}>{usd(r.priceCents)}</td>
                  <td className={NUM}>{(r.capacityCuft as number).toFixed(1)}</td>
                  <td className={NUM}>{r.rating.toFixed(2)} / 5</td>
                  <td className={NUM}>{int(r.reviews)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  )
}

export function BrandsByNumbersSection({ ranking }: { ranking: BestRefrigeratorsRanking }) {
  if (ranking.brands.length === 0) return null
  return (
    <div className={PAGE_W}>
      <section className="my-12" aria-labelledby="brands-by-the-numbers">
        <PageSectionHeader eyebrow="Brands by the Numbers" title="Refrigerator Brands 2026 by Owner Rating" />
        <p className="text-sm text-gray-600 leading-relaxed mb-4 max-w-3xl">
          Every brand with at least {BRAND_TABLE_MIN_RATED} refrigerators in the ranked set, as of {ranking.asOfLabel}. Rated models, median owner rating and owner reviews come from that set.
          Median advertised price and median certified annual energy use come from the{" "}
          <Link href="/data/brand-index" className="text-[#0469f0] underline">Fridge.com Refrigerator Brand Index</Link> ({ranking.brandIndexAsOfLabel}); a dash means the brand is not listed there or has fewer certified models than the index publishes a figure for.
          Fridge.com does not grade brand reliability.
        </p>
        <div className="overflow-x-auto rounded-xl border border-gray-100 bg-white">
          <table className="w-full text-sm" id="brands-by-the-numbers">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className={TH}>Brand</th>
                <th scope="col" className={`${TH} text-right`}>Rated models</th>
                <th scope="col" className={`${TH} text-right`}>Median owner rating</th>
                <th scope="col" className={`${TH} text-right`}>Owner reviews</th>
                <th scope="col" className={`${TH} text-right`}>Median advertised price</th>
                <th scope="col" className={`${TH} text-right`}>Certified kWh/yr (median)</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {ranking.brands.map((b) => (
                <tr key={b.brand}>
                  <td className={`${TD} font-semibold text-gray-900`}>
                    {b.href ? <Link href={b.href} className="hover:text-[#0469f0]">{b.brand}</Link> : b.brand}
                  </td>
                  <td className={NUM}>{int(b.ratedModels)}</td>
                  <td className={NUM}>{b.medianRating.toFixed(2)} / 5</td>
                  <td className={NUM}>{int(b.totalReviews)}</td>
                  <td className={NUM}>{usdWhole(b.medianPrice)}</td>
                  <td className={NUM}>{b.certifiedKwh === null ? "—" : `${int(Math.round(b.certifiedKwh))} (${int(b.certifiedN)} models)`}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  )
}
